import type { ElementRef } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';

import { ChangeDetectionStrategy, Component, computed, inject, input, viewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

export enum InformationBannerType {
  Primary = 'primary',
  Secondary = 'secondary',
  Success = 'success',
  Warning = 'warning',
  Info = 'info',
  Danger = 'danger',
}

@Component({
  selector: 'app-information-banner',
  standalone: true,
  imports: [MatIconModule],
  template: `
    <span
      class="information-banner"
      [class.information-banner--primary]="type() === InformationBannerType.Primary"
      [class.information-banner--secondary]="type() === InformationBannerType.Secondary"
      [class.information-banner--success]="type() === InformationBannerType.Success"
      [class.information-banner--warning]="type() === InformationBannerType.Warning"
      [class.information-banner--info]="type() === InformationBannerType.Info"
      [class.information-banner--danger]="type() === InformationBannerType.Danger"
      [class.information-banner--border]="hasBorder()"
    >
      <div class="information-banner__text">
        @if (withIcon()) {
          <div [innerHTML]="icon"></div>
        }
        <ng-content select="[content]" />
      </div>
      <div #action>
        <ng-content select="[action]" />
      </div>
      <ng-content />
    </span>
  `,
  styleUrls: ['./information-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InformationBannerComponent {
  readonly type = input<InformationBannerType>(InformationBannerType.Info);
  readonly withIcon = input<boolean>(false);
  readonly action = viewChild<ElementRef<HTMLDivElement>>('action');

  readonly #sanitizer = inject(DomSanitizer);

  readonly InformationBannerType = InformationBannerType;

  readonly hasBorder = computed(() => {
    return this.action()?.nativeElement.children.length === 0;
  });

  get icon(): SafeHtml {
    switch (this.type()) {
      case InformationBannerType.Primary:
        return this.#sanitizer
          .bypassSecurityTrustHtml(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 22.25H9C6.3424 22.25 4.56492 21.6803 3.4423 20.5577C2.31968 19.4351 1.75 17.6576 1.75 15L1.75 9C1.75 6.3424 2.31968 4.56492 3.4423 3.4423C4.56492 2.31968 6.3424 1.75 9 1.75L15 1.75C17.6576 1.75 19.4351 2.31968 20.5577 3.4423C21.6803 4.56492 22.25 6.3424 22.25 9V15C22.25 17.6576 21.6803 19.4351 20.5577 20.5577C19.4351 21.6803 17.6576 22.25 15 22.25ZM9 2.25C6.65972 2.25 4.92432 2.66107 3.7927 3.7927C2.66107 4.92432 2.25 6.65972 2.25 9L2.25 15C2.25 17.3403 2.66107 19.0757 3.7927 20.2073C4.92432 21.3389 6.65972 21.75 9 21.75H15C17.3403 21.75 19.0757 21.3389 20.2073 20.2073C21.3389 19.0757 21.75 17.3403 21.75 15V9C21.75 6.65972 21.3389 4.92432 20.2073 3.7927C19.0757 2.66107 17.3403 2.25 15 2.25L9 2.25Z" fill="#5D5871" stroke="#5D5871"/>
        <path d="M7.17902 14.6849L7.17907 14.6849L7.17204 14.6796C7.06719 14.6001 7.04202 14.4457 7.12776 14.3312L9.50485 11.245C9.71634 10.9759 10.0088 10.8068 10.3434 10.7641L10.3448 10.7639C10.6698 10.7215 11.0055 10.8132 11.2713 11.0214C11.2714 11.0215 11.2716 11.0215 11.2717 11.0216L13.0946 12.4561C13.2927 12.6204 13.5065 12.6362 13.6547 12.6157C13.8356 12.6001 14.0271 12.5065 14.1672 12.322L16.4753 9.34438L16.4753 9.3444L16.4786 9.33998C16.5599 9.23274 16.7169 9.21654 16.8148 9.29611L16.8214 9.30147L16.8282 9.3066C16.9327 9.38576 16.9561 9.53603 16.8674 9.64862L16.8649 9.65173L14.5566 12.6297C14.5563 12.63 14.5561 12.6303 14.5558 12.6306C14.3439 12.9005 14.0541 13.0661 13.7272 13.1009L13.7272 13.1008L13.7168 13.1021C13.3815 13.1449 13.0551 13.0534 12.7889 12.8449C12.7888 12.8448 12.7887 12.8447 12.7885 12.8446L10.9654 11.41C10.7744 11.2519 10.5567 11.2277 10.3998 11.251C10.2203 11.2681 10.0311 11.3619 9.89246 11.545L7.51399 14.633L7.50179 14.6488L7.49089 14.6656C7.46657 14.703 7.41711 14.7381 7.33011 14.7381C7.27758 14.7381 7.22741 14.723 7.17902 14.6849Z" fill="#5D5871" stroke="#5D5871"/>
        </svg>
        `);
      case InformationBannerType.Secondary:
        return this.#sanitizer
          .bypassSecurityTrustHtml(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 11V17C22 21 21 22 17 22H7C3 22 2 21 2 17L2 7C2 3 3 2 7 2H8.5C10 2 10.33 2.44 10.9 3.2L12.4 5.2C12.78 5.7 13 6 14 6H17C21 6 22 7 22 11Z" stroke="#5D5871" stroke-width="1.5" stroke-miterlimit="10"/>
          <path d="M8 2L17 2C19 2 20 3 20 5V6.38" stroke="#5D5871" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `);
      case InformationBannerType.Success:
        return this.#sanitizer
          .bypassSecurityTrustHtml(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.0605 16.5V11.5" stroke="#5D5871" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14.5 14H9.5" stroke="#5D5871" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M22 11V17C22 21 21 22 17 22H7C3 22 2 21 2 17L2 7C2 3 3 2 7 2H8.5C10 2 10.33 2.44 10.9 3.2L12.4 5.2C12.78 5.7 13 6 14 6H17C21 6 22 7 22 11Z" stroke="#5D5871" stroke-width="1.5" stroke-miterlimit="10"/>
          </svg>
        `);
      case InformationBannerType.Warning:
        return this.#sanitizer
          .bypassSecurityTrustHtml(`<svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity=".3" fill-rule="evenodd" clip-rule="evenodd" d="M11.9 4.21a1 1 0 0 0-1.73.04L2.66 17.76a1 1 0 0 0 .87 1.49H19.3a1 1 0 0 0 .85-1.52L11.9 4.2Z" fill="#FF7C00"/>
          <rect x="10.08" y="8.25" width="1.83" height="6.42" rx=".92" fill="#fff"/>
          <rect x="10.08" y="15.58" width="1.83" height="1.83" rx=".92" fill="#fff"/>
          </svg>
        `);
      case InformationBannerType.Info:
        return this.#sanitizer
          .bypassSecurityTrustHtml(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 8.75H2C1.86614 8.75 1.75 8.63386 1.75 8.5C1.75 8.36614 1.86614 8.25 2 8.25H22C22.1339 8.25 22.25 8.36614 22.25 8.5C22.25 8.63386 22.1339 8.75 22 8.75Z" fill="#0045FF" stroke="#0045FF"/>
          <path d="M8 16.75H6C5.86614 16.75 5.75 16.6339 5.75 16.5C5.75 16.3661 5.86614 16.25 6 16.25H8C8.13386 16.25 8.25 16.3661 8.25 16.5C8.25 16.6339 8.13386 16.75 8 16.75Z" fill="#0045FF" stroke="#0045FF"/>
          <path d="M14.5 17.25H10.5C10.09 17.25 9.75 16.91 9.75 16.5C9.75 16.09 10.09 15.75 10.5 15.75H14.5C14.91 15.75 15.25 16.09 15.25 16.5C15.25 16.91 14.91 17.25 14.5 17.25Z" fill="#0045FF"/>
          <path d="M17.56 21.25H6.44C2.46 21.25 1.25 20.05 1.25 16.11L1.25 7.89C1.25 3.95 2.46 2.75 6.44 2.75L17.55 2.75C21.53 2.75 22.74 3.95 22.74 7.89V16.1C22.75 20.05 21.54 21.25 17.56 21.25ZM6.44 4.25C3.3 4.25 2.75 4.79 2.75 7.89L2.75 16.1C2.75 19.2 3.3 19.74 6.44 19.74H17.55C20.69 19.74 21.24 19.2 21.24 16.1V7.89C21.24 4.79 20.69 4.25 17.55 4.25H6.44Z" fill="#0045FF"/>
          </svg>
        `);
      case InformationBannerType.Danger:
        return this.#sanitizer
          .bypassSecurityTrustHtml(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3.17188 7.44141L12.0019 12.5514L20.7719 7.47141" stroke="#C6242E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 21.6091V12.5391" stroke="#C6242E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2.39062 13.2486V14.8286C2.39062 16.2086 3.38064 17.8886 4.59064 18.5586L9.93062 21.5286C11.0706 22.1586 12.9406 22.1586 14.0806 21.5286L19.4206 18.5586C20.6306 17.8886 21.6206 16.2086 21.6206 14.8286V9.16859C21.6206 7.78859 20.6306 6.10859 19.4206 5.43859L14.0806 2.46859C12.9406 1.83859 11.0706 1.83859 9.93062 2.46859L4.59064 5.43859C3.38064 6.10859 2.39062 7.78859 2.39062 9.16859" stroke="#C6242E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `);
    }
  }
}
